/**
 * The toast stack — the half of DESIGN.md §11 rule 4 that makes the other half possible.
 *
 * "Aldri en bekreftelsesdialog for noe som kan angres." That rule only works if there is
 * somewhere for the undo to live. So a toast here is not a decoration: it is the receipt for an
 * optimistic action, and the `action` it carries is what lets a stage manager remove the wrong
 * row at 23:00 and put it back without a dialog having interrupted the fifty times they removed
 * the right one.
 *
 * Behaviour:
 *   - Stacks, newest nearest the corner it enters from, capped so the screen never fills.
 *   - Auto-dismisses, and **pauses on hover and on focus** — the countdown must not run out
 *     while someone is reaching for "undo" or tabbing to it.
 *   - `role="status"` for ordinary news, `role="alert"` for a failure.
 *
 * WHAT A TOAST IS NOT: a toast is never the only way a user learns that something failed. It is
 * transient, it can be missed, and on a festival site it will be missed. A failed save also
 * leaves its error on the field (`Field`/`aria-describedby`), a failed list load also renders
 * `ErrorState` in place of the list. The toast is the fast notice, never the record.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';

import { ICONS, type IconName } from './icons';
import { noteToastShown, setToastSink } from './toast-sink';

export type ToastTone = 'neutral' | 'success' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  onAction: () => void;
  /** `show` for en handling som viser noe (F47: «Vis»), uten angrepilen. Standard er angre. */
  kind?: 'undo' | 'show';
}

export interface ToastOptions {
  /** Already translated. A toast never assembles a sentence itself. */
  title: string;
  description?: string;
  tone?: ToastTone;
  /** The undo affordance. Present it whenever the action it reports can be reversed. */
  action?: ToastAction;
  /** Dwell time in ms, or `null` to stay until dismissed. Defaults by tone. */
  duration?: number | null;
}

export interface ToastRecord extends ToastOptions {
  id: string;
}

export interface ToastApi {
  /** Shows a toast and returns its id. */
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

/*
 * Dwell times, not motion durations — these are how long a human needs to READ something, so
 * they are not in `tokens.ts` (which owns how long a thing takes to MOVE, DESIGN.md §6).
 * A failure is given longer because it is the one a reader cannot afford to miss, and a toast
 * carrying an undo is given longer still because it is an invitation to act, not just to read.
 */
const DWELL_DEFAULT_MS = 6000;
/*
 * 7 SEKUNDER, IKKE 9 (QA-runde 12, J2).
 *
 * «Toast nederst med kort tekst og Angre i ca. 5–8 sekunder.» Tallet sto på 9000 fordi lengre
 * alltid ser tryggere ut, men en angre-knapp som blir stående i ni sekunder er også ni sekunder der
 * neste handlings kvittering ikke får plass — og stabelen er fire høy. Sju er midt i vinduet Atle
 * ba om, og pausen på hover og fokus gjør at den som faktisk strekker seg etter knappen aldri
 * taper kappløpet.
 */
const DWELL_WITH_ACTION_MS = 7000;
const DWELL_ERROR_MS = 12000;

/** Beyond four, the stack stops being readable and starts being a wall. Oldest falls off. */
const MAX_VISIBLE = 4;

const TONE_STYLE: Record<ToastTone, { icon: IconName; accent: string }> = {
  neutral: { icon: 'alert', accent: 'text-muted' },
  success: { icon: 'check', accent: 'text-[color:var(--color-success)]' },
  warning: { icon: 'warning', accent: 'text-[color:var(--color-warning)]' },
  error: { icon: 'warning', accent: 'text-[color:var(--color-danger)]' },
};

function defaultDuration(options: ToastOptions): number {
  if (options.tone === 'error') return DWELL_ERROR_MS;
  return options.action ? DWELL_WITH_ACTION_MS : DWELL_DEFAULT_MS;
}

const ToastContext = createContext<ToastApi | null>(null);

let sequence = 0;

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  /*
   * PAUSEN EIES AV EN TOAST, IKKE AV STABELEN (QA-runde 13, punkt 44 og 90).
   *
   * Pausen sto som ett flagg på regionen, satt av `mouseenter`/`focus` og løst av `mouseleave`/
   * `blur`. Men når toasten som har pekeren eller fokuset forsvinner — lukket med krysset, «Angre»,
   * eller presset ut av stabelen på fire — sender nettleseren ingen av de to. Flagget ble stående,
   * og hver senere toast ble liggende til noen tilfeldigvis førte musa gjennom hjørnet (målt: den
   * neste sto fortsatt etter 15 s). Nå husker vi HVILKE toaster som holder pausen, og stabelen står
   * bare så lenge minst én av dem fortsatt finnes. En toast som går, tar pausen sin med seg.
   */
  const [holders, setHolders] = useState<ReadonlyMap<string, PauseHold>>(new Map());
  const paused = toasts.some((entry) => {
    const hold = holders.get(entry.id);
    return hold !== undefined && (hold.hover || hold.focus);
  });

  const hold = useCallback((id: string, kind: keyof PauseHold, on: boolean) => {
    setHolders((current) => {
      const previous = current.get(id) ?? { hover: false, focus: false };
      if (previous[kind] === on) return current;
      const next = new Map(current);
      const changed = { ...previous, [kind]: on };
      if (changed.hover || changed.focus) next.set(id, changed);
      else next.delete(id);
      return next;
    });
  }, []);

  /* Rydder holdene til toaster som er borte, så kartet ikke vokser over en lang økt. */
  useEffect(() => {
    setHolders((current) => {
      const alive = new Set(toasts.map((entry) => entry.id));
      if ([...current.keys()].every((id) => alive.has(id))) return current;
      return new Map([...current].filter(([id]) => alive.has(id)));
    });
  }, [toasts]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const dismissAll = useCallback(() => setToasts([]), []);

  const toast = useCallback((options: ToastOptions) => {
    noteToastShown();
    sequence += 1;
    const id = `toast-${sequence}`;
    setToasts((current) => [...current, { ...options, id }].slice(-MAX_VISIBLE));
    return id;
  }, []);

  const api = useMemo<ToastApi>(
    () => ({ toast, dismiss, dismissAll }),
    [toast, dismiss, dismissAll],
  );

  /*
   * Broen til det som ikke er en komponent — se `toast-sink.ts`. Den finnes for den globale
   * `onError` i `MutationCache`, som lages utenfor React og ellers ikke kunne si fra om noe.
   */
  useEffect(() => {
    setToastSink(toast);
    return () => {
      setToastSink(null);
    };
  }, [toast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/*
        Always mounted, so the region exists before the first toast does — an assistive
        technology that has to discover a live region at the moment it changes announces
        nothing at all.
      */}
      <div
        role="region"
        aria-label={t('toast.region')}
        data-testid="toast-region"
        data-toast-region=""
        data-paused={paused ? '' : undefined}
        // Never `hidden`: display:none takes the live region out of the accessibility tree, and a
        // live region discovered at the moment it changes announces nothing. It stays, inert.
        /*
         * ALDRI BAK ET SIDEPANEL (sak 215.24). To slags «bak», og begge er stengt:
         *
         * - SYNLIG: stabelen står over panelet (z-60 mot 50) og VED SIDEN AV det — nederst til
         *   venstre, der den verken dekker «Lagre» eller innholdet (QA5-D målte toastens «Lukk» midt
         *   på panelets «Lagre» da den sto i hjørnet). På en smal skjerm fyller panelet bredden, og
         *   da går stabelen til toppen, så panelets knapper nederst står fri.
         * - FOR SKJERMLESEREN: Radix gjør alt utenfor et åpent panel `aria-hidden`, og «Angre» ble
         *   borte for den som ikke ser. `aria-hidden` (pakken Radix bruker) hopper over alt med
         *   `aria-live`, så regionen får attributtet. «off» fordi hver toast er sin egen levende
         *   region (`status`/`alert`); en «polite» her ville lest dem to ganger.
         */
        aria-live="off"
        className={cn(
          'pointer-events-none fixed right-0 bottom-0 z-[60] flex w-full max-w-sm flex-col gap-2 p-3',
          '[body:has([data-placement=right][data-state=open])_&]:right-auto',
          '[body:has([data-placement=right][data-state=open])_&]:left-0',
          'max-sm:[body:has([data-placement=right][data-state=open])_&]:top-0',
          'max-sm:[body:has([data-placement=right][data-state=open])_&]:bottom-auto',
        )}
      >
        {toasts.map((entry) => (
          <ToastItem
            key={entry.id}
            toast={entry}
            paused={paused}
            onHold={hold}
            onDismiss={dismiss}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** Hva som holder stabelen i ro: pekeren over toasten, fokus inni den, eller begge. */
interface PauseHold {
  hover: boolean;
  focus: boolean;
}

interface ToastItemProps {
  toast: ToastRecord;
  paused: boolean;
  onHold: (id: string, kind: keyof PauseHold, on: boolean) => void;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, paused, onHold, onDismiss }: ToastItemProps): ReactNode {
  const { t } = useTranslation();
  const tone = toast.tone ?? 'neutral';
  const { icon, accent } = TONE_STYLE[tone];
  const Icon = ICONS[icon];
  const CloseIcon = ICONS.close;

  const total = toast.duration === undefined ? defaultDuration(toast) : toast.duration;
  /** What is left of the dwell time. Survives a pause, so hovering does not reset the clock. */
  const remaining = useRef<number>(total ?? 0);
  /*
   * ETTER KLIKKET, IKKE UNDER DET (QA5-D). Et åpent panel (Radix) avgjør «klikk utenfor» først på
   * `click` på dokumentet — etter at knappens egen håndterer har kjørt. Fjernet toasten seg selv
   * der, var knappen allerede ute av DOM-en, `Modal` kunne ikke se at klikket kom fra toasten, og
   * «Angre» lukket panelet det angret i. Et tikk senere står den fortsatt der når spørsmålet stilles.
   */
  const dismissAfterClick = (): void => {
    window.setTimeout(() => onDismiss(toast.id), 0);
  };

  useEffect(() => {
    if (total === null || paused) return undefined;
    const startedAt = Date.now();
    const timer = window.setTimeout(() => onDismiss(toast.id), remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt));
    };
  }, [paused, total, toast.id, onDismiss]);

  return (
    <div
      // A failure is assertive; everything else waits its turn rather than interrupting.
      role={tone === 'error' ? 'alert' : 'status'}
      aria-atomic="true"
      data-tone={tone}
      data-testid="toast"
      // Pause on hover AND on focus, for the whole stack — someone reaching for «Angre» on one
      // toast must not watch its neighbour slide under the pointer. React's onFocus/onBlur bubble,
      // so tabbing between the two buttons inside one toast is a blur and a focus in the same tick.
      onMouseEnter={() => onHold(toast.id, 'hover', true)}
      onMouseLeave={() => onHold(toast.id, 'hover', false)}
      onFocus={() => onHold(toast.id, 'focus', true)}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        onHold(toast.id, 'focus', false);
      }}
      className={cn(
        'glass-popover pointer-events-auto flex items-start gap-2.5',
        'px-3 py-2.5',
        'animate-[tvitvi-toast-in_var(--duration-base)_var(--ease-out)]',
      )}
    >
      {/* Never colour alone (DESIGN.md §3): the tone has a glyph as well as a hue. */}
      <Icon aria-hidden className={cn('mt-px size-3.5 shrink-0', accent)} />

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-default">{toast.title}</p>
        {toast.description ? (
          <p className="mt-0.5 text-sm leading-snug text-muted">{toast.description}</p>
        ) : null}
        {toast.action ? (
          <button
            type="button"
            data-testid="toast-action"
            onClick={() => {
              toast.action?.onAction();
              dismissAfterClick();
            }}
            className={cn(
              /* Tonal, uten kant: kant betyr inndatafelt (G6). */
              'hit-target mt-1.5 inline-flex h-7 items-center gap-1.5 rounded-full',
              'bg-[var(--fill-tonal)] px-3 text-xs font-medium text-default ring-focus',
              'transition-colors duration-[var(--duration-fast)] hover:bg-[var(--fill-tonal-hover)]',
            )}
          >
            {toast.action.kind === 'show' ? null : <UndoIcon />}
            {toast.action.label}
          </button>
        ) : null}
      </div>

      <button
        type="button"
        aria-label={t('common.actions.close')}
        onClick={dismissAfterClick}
        className={cn(
          'hit-target -mt-0.5 -mr-0.5 inline-flex size-7 shrink-0 items-center justify-center',
          'rounded-full text-subtle ring-focus',
          'transition-colors duration-[var(--duration-fast)] hover:bg-[var(--fill-tonal)] hover:text-default',
        )}
      >
        <CloseIcon aria-hidden className="size-3.5" />
      </button>
    </div>
  );
}

function UndoIcon(): ReactNode {
  const Icon = ICONS.undo;
  return <Icon aria-hidden className="size-3" />;
}

export function useToast(): ToastApi {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used inside <ToastProvider>.');
  return value;
}
